/**
 * Detecção de capacidade de plataforma. Puro, sem React, SSR-safe: fora do
 * browser TODOS devolvem `false`.
 *
 * Existe como módulo porque a mesma pergunta é feita em quatro lugares
 * (dispatcher de notificação, registro de SW, badge e UI de instalação) e a
 * resposta errada é silenciosa nos quatro.
 */

/**
 * A interface `Notification` existe?
 *
 * **Não** é "tem permissão" nem "funciona". Na ABA do Safari iOS a interface é
 * `undefined` — a plataforma não oferece notificação fora do app salvo na tela
 * de início (P10). No Chrome Android ela EXISTE e o construtor lança; quem trata
 * isso é `lib/notifications/dispatch`.
 */
export function hasNotificationApi(): boolean {
  return typeof window !== 'undefined' && typeof window.Notification !== 'undefined'
}

export function hasServiceWorker(): boolean {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator
}

/**
 * `navigator.setAppBadge` existe?
 *
 * `false` no Chrome Android e em qualquer desktop Linux, **por contrato de
 * plataforma** (BCD `Navigator.setAppBadge.chrome_android: false`; o Chrome
 * documenta que no Android o SO desenha o ponto sozinho quando há notificação
 * não lida). Ausência aqui é no-op silencioso esperado, nunca bug a investigar.
 */
export function hasAppBadge(): boolean {
  return typeof navigator !== 'undefined' && 'setAppBadge' in navigator
}

/** Safari do iOS expõe standalone por uma propriedade não-padrão. */
type IosNavigator = Navigator & { standalone?: boolean }

/**
 * O app está rodando instalado (janela própria), e não numa aba?
 *
 * Consulta os dois sinais de propósito: `display-mode: standalone` é o padrão, e
 * `navigator.standalone` é o que o Safari do iOS responde. Um só já mentiu em
 * iPhone.
 */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false
  if (typeof window.matchMedia === 'function') {
    if (window.matchMedia('(display-mode: standalone)').matches) return true
  }
  return (navigator as IosNavigator).standalone === true
}

/**
 * O aparelho é móvel, segundo o Client Hint do próprio browser.
 *
 * **Não é feature detection, e por isso é a última escolha, não a primeira.**
 * Existe porque há uma diferença de RENDERIZAÇÃO de notificação que nenhuma
 * capacidade expõe: o Chrome Android desenha o ícone do app na notificação
 * sozinho, então mandar `icon` junto mostra a mesma logo duas vezes; o Chrome
 * desktop faz o oposto — sem `icon` ele exibe o ícone do Chrome no lugar da
 * marca. Medido em bancada nos dois, 2026-09-04, inclusive com `badge`
 * monocromático declarado, que não suprime a duplicata.
 *
 * `navigator.userAgentData.mobile` é o Client Hint desenhado exatamente para
 * esta pergunta, e é bem mais estreito que farejar a string de User-Agent.
 * Ausente (Safari) devolve `false`, o que é inofensivo: pela BCD o Safari
 * ignora `icon` de qualquer jeito ("Property can be set, but has no effect").
 */
export function isMobilePlatform(): boolean {
  if (typeof navigator === 'undefined') return false
  const dados = (navigator as Navigator & { userAgentData?: { mobile?: boolean } })
    .userAgentData
  return dados?.mobile === true
}
